import * as React from 'react';
import { withStyles } from '@material-ui/core/styles';
import styles from './GenericDetailsForm.styles';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { IActionItem } from 'components/common/Modals/FormViewModal.properties';
import IGenericDetailsFormProps from './GenericDetailsForm.properties';
import NumericField from '@markinson/uicomponents-v2/NumericField';
import TextField from '@markinson/uicomponents-v2/TextField';
import ITextFieldHandle from '@markinson/uicomponents-v2/TextField/TextField.handle';

const GenericDetailsForm = (props: IGenericDetailsFormProps): JSX.Element => {
  const { classes, open, title, initialValues, schema, isProcessing, onApply, onCancel } = props;

  const [amount, setAmount] = React.useState<number>(0);
  const [reference, setReference] = React.useState<string>('');

  const paymentAmountFieldRef = React.useRef<ITextFieldHandle>();
  const referenceFieldRef = React.useRef<ITextFieldHandle>();

  React.useEffect(
    () => {
      if (initialValues) {
        setAmount(initialValues.Amount ? initialValues.Amount : 0);
        setReference(initialValues.Reference);
      } else {
        setAmount(0);
        setReference('');
      }
    },
    [initialValues]);

  const getModalContent = () => {
    return <div className={classes.mainDiv}>
      <NumericField
        ref={paymentAmountFieldRef}
        value={amount}
        label={'Payment Amount'}
        minValueExclusive={0}
        onBlur={(value) => { setAmount(value); }}
        required={schema.Amount.Required} />
      <TextField
        innerRef={referenceFieldRef}
        value={reference}
        label={'Reference'}
        onChange={(value) => { setReference(value); }}
        required={schema.Reference.Required} />
    </div >;
  };

  const confirmationActions = (): IActionItem[] => {
    return [
      {
        title: 'Ok',
        isDefault: true,
        disabled: isProcessing,
        listener: handleOnApply
      },
      {
        title: 'Cancel',
        listener: async () => {
          if (onCancel) {
            onCancel();
          }
        }
      }
    ];
  };

  const hasErrors = () => {
    if ((schema.Amount.Required && amount === undefined) || (amount !== undefined && amount <= 0) ||
      (schema.Reference.Required && !reference)) {

      return true;
    }

    return false;
  };

  const focusField = () => {
    setTimeout(
      () => {
        paymentAmountFieldRef?.current?.focus();
      },
      0
    );
  };

  const handleOnApply = async () => {
    if (!hasErrors()) {
      if (onApply) {
        onApply({
          ...initialValues,
          Amount: amount,
          Reference: reference
        });
      }
    } else {
      referenceFieldRef.current?.validate();
    }
  };

  React.useEffect(
    () => {
      const handleKeyDown = (event) => {
        const isCancelButton = event.target instanceof HTMLButtonElement && event.target.innerText === 'Cancel';

        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();

          if (isCancelButton && onCancel) {
            onCancel();

            return;
          }

          if (event.target && typeof event.target.blur === 'function') {
            event.target.blur();
          }

          if (!hasErrors()) {
            handleOnApply();
          } else {
            referenceFieldRef.current?.validate();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    },
    [hasErrors, handleOnApply]
  );

  return (<FormViewModal
    open={open}
    loading={false}
    title={title}
    modalContent={getModalContent()}
    actions={confirmationActions()}
    dialogActionsButtons={true}
    dialogActionsShadow={false}
    onActionShiftTabOut={focusField}
    onActionsTabOut={focusField}
  />);
};

export default withStyles(styles, { index: 1 })(GenericDetailsForm);
